import type { Session } from '@mesa/core/auth/better-auth';
import { Link, useRouterState } from '@tanstack/react-router';
import { Icon } from '@/components/ui/icon';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import type { OrgSummary } from '@/hooks/use-org';
import { OrgSwitcher } from './org-switcher';
import { UserMenu } from './user-menu';

export const navItems = [
  {
    title: 'API Tokens',
    path: 'tokens',
    icon: 'key',
  },
  {
    title: 'Repositories',
    path: 'repositories',
    icon: 'book-bookmark',
  },
  {
    title: 'Members',
    path: 'members',
    icon: 'users',
  },
  {
    title: 'Settings',
    path: 'settings',
    icon: 'gear',
  },
];

const DOCS_URL = 'https://docs.mesa.dev';

interface AppSidebarProps {
  session: Session;
  currentOrg: OrgSummary;
  orgs: OrgSummary[];
  gitBaseUrl: string;
  onOrgUpdated?: () => void;
}

export function AppSidebar({ session, currentOrg, orgs, gitBaseUrl, onOrgUpdated }: AppSidebarProps) {
  const { location } = useRouterState();
  const { isCollapsed } = useSidebar();

  return (
    <Sidebar>
      <SidebarHeader>
        <OrgSwitcher currentOrg={currentOrg} orgs={orgs} gitBaseUrl={gitBaseUrl} onOrgUpdated={onOrgUpdated} />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                const href = `/${currentOrg.slug}/${item.path}`;
                return (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton asChild isActive={location.pathname === href}>
                      <Link to={href}>
                        <Icon name={item.icon} />
                        {!isCollapsed && <span>{item.title}</span>}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <a href={DOCS_URL} target="_blank" rel="noopener noreferrer">
                <Icon name="book-open" />
                {!isCollapsed && <span>Documentation</span>}
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarSeparator />
        <UserMenu session={session} />
      </SidebarFooter>
    </Sidebar>
  );
}
